import { Badge, Flex, Group, Stack, createStyles } from '@mantine/core';
import { IconEdit, IconLock } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';

import type { BaseField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { TableType } from '../Table';
import TableDescriptor from '../Table';

type AdditionalContentType = TableType;

export interface SectionsSummaryType extends BaseField {
  type: 'SectionsSummary';
  data: {
    sectionKey: string;
    label?: string;
    subLabel?: string;
    goToEdit?: boolean;
    locked?: string;
    fields: {
      fieldKey: string;
      value: string;
    }[];
    additionalContent?: AdditionalContentType[];
  }[];
}

const useStyles = createStyles((theme) => ({
  section: {
    padding: `${theme.spacing.md}px 0`,

    '&:first-child': {
      paddingTop: 0,
    },
  },
  goToEdit: {
    '&:first-of-type': {
      borderTop: 'none',
    },

    padding: 0,
    borderTop: `1px solid ${theme.colors.gray2[1]}`,
    paddingBottom: theme.other.spacing._8,
  },
  segmentHeader: {
    backgroundColor: theme.colors.gray[5],
  },
  edit: {
    cursor: 'pointer',
  },
  list: {
    margin: `0 0 ${theme.spacing.xs}px`,
  },
  sectionLink: {
    order: 3,
    [theme.fn.smallerThan('sm')]: {
      order: 2,
      marginTop: -theme.other.spacing._4,
    },
  },
}));

function SectionSummaryFieldValue({ value, isLocked }: { value: string; isLocked?: boolean }) {
  const { theme } = useStyles();

  if (!value) return null;

  return (
    <Badge
      color="gray2.0"
      bg={theme.colors.gray2[9]}
      tt="capitalize"
      rightSection={
        isLocked ? (
          <Flex align="center" justify="center">
            <IconLock size={14} color={theme.white} />
          </Flex>
        ) : null
      }
    >
      {value}
    </Badge>
  );
}

function SectionsSummaryElement({ field, onGoToSection }: StaticFieldProps<SectionsSummaryType>) {
  const { classes, cx } = useStyles();

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={5}>
      {field.data?.map((section) => (
        <div
          key={section.sectionKey}
          className={cx(classes.section, {
            [classes.goToEdit]: section.goToEdit,
          })}
        >
          <Stack spacing={0}>
            <Stack spacing={0} /* eslint-disable-line local-rules/use-theme-tokens */ mb={12} justify="space-between">
              {section.label ? (
                <Text
                  data-test-id={dt.summary.label(section.label)}
                  variant="subhead-small"
                  transform="uppercase"
                >
                  {section.label}
                </Text>
              ) : null}
              {section.subLabel ? (
                <Text data-test-id={dt.summary.label(section.subLabel)} variant="body-medium">
                  {section.subLabel}
                </Text>
              ) : null}
            </Stack>

            <Flex justify="space-between" direction="row" align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={10}>
              <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4} w="100%">
                {section.fields.map((f) => (
                  <div key={f.fieldKey} data-test-id={dt.summary.field(f.fieldKey)}>
                    <SectionSummaryFieldValue value={f.value} isLocked={!!section.locked} />
                  </div>
                ))}
              </Group>

              {!section.locked && section.goToEdit ? (
                <IconEdit
                  size={20}
                  className={classes.edit}
                  data-test-id={dt.summary.action.change}
                  onClick={() => {
                    onGoToSection(section.sectionKey);
                  }}
                />
              ) : null}
            </Flex>
          </Stack>
          {section.additionalContent && (
            <Stack spacing="xs">
              {section.additionalContent.map((content) => (
                <TableDescriptor.FieldComponent field={content} key={content.key} isMetaHidden />
              ))}
            </Stack>
          )}
        </div>
      ))}
    </Stack>
  );
}

const Descriptor: FieldDescriptor<SectionsSummaryType> = {
  FieldComponent: SectionsSummaryElement,
  name: 'SectionsSummary',
};

export default Descriptor;
